/**
 * Single source of truth for pathname-derived chrome classes.
 *
 * Consumed by BOTH the React hooks/components (use-immersive-route,
 * PlatformShell, contextual sidebar registry) AND the generated pre-paint
 * inline scripts (pre-paint-scripts.ts) — keeping the paint-time and
 * hydration-time classifications identical by construction. Never inline a
 * route regex in a chrome component or script; add it here.
 */

/** Immersive routes: transparent header, collapsed (offcanvas) sidebar. */
export const IMMERSIVE_PATTERNS: RegExp[] = [
  /^\/videos\/[^/]+$/, // /videos/[id]
  /^\/series\/[^/]+\/play\//, // /series/[slug]/play/[videoUuid]
  /^\/courses\/[^/]+\/play\//, // /courses/[id]/play/[videoUuid]
  /^\/courses\/(?!bundles(?:\/|$))[^/]+$/, // course detail (excludes /courses/bundles subtree)
  /^\/courses\/bundles\/[^/]+$/, // bundle detail
  /^\/trailer$/,
  /^\/shorts(\/|$)/, // shorts feed never window-scrolls; header stays transparent
];

export const STUDIO_IMMERSIVE_PATTERNS: RegExp[] = [/^\/studio\/watch/];

/** Guests only — members keep the solid header on these routes. */
export const GUEST_IMMERSIVE_PATTERNS: RegExp[] = [
  /^\/creators(\/|$)/,
  /^\/courses\/(bundles\/)?\d+(\/|$)/, // course/bundle detail (OTP gate)
];

/** Routes that show the decorative background glow. */
export const GLOW_PATTERNS: RegExp[] = [/^\/creators$/];

/**
 * Full-height surfaces (internal scroll / swipe feed) — a trailing footer
 * would be unreachable or break their geometry. Prefix semantics.
 */
export const FULL_SURFACE_PATTERNS: RegExp[] = [/^\/ai/, /^\/shorts/, /^\/messages/];

export const HIDDEN_SEARCH_PATTERNS: RegExp[] = [/^\/globe$/];

/** /ai: the app sidebar morphs into the Taboo Terra Chats sidebar (AppSidebar). */
export const AI_CHATS_PATTERN = /^\/ai\/?$/;

/**
 * Routes whose sidebar swaps its content: the studio entity editors
 * (resolveContextualSidebar, capture group 1 = entity param) and /ai (Chats).
 */
export const CONTEXTUAL_COURSE_PATTERN = /^\/studio\/courses\/([^/]+)\//;
export const CONTEXTUAL_SERIES_PATTERN = /^\/studio\/series\/([^/]+)\//;
export const CONTEXTUAL_VIDEO_PATTERN = /^\/studio\/videos\/([^/]+)\//;
export const CONTEXTUAL_SIDEBAR_PATTERNS: RegExp[] = [
  CONTEXTUAL_COURSE_PATTERN,
  CONTEXTUAL_SERIES_PATTERN,
  CONTEXTUAL_VIDEO_PATTERN,
  AI_CHATS_PATTERN,
];

export function matchesAny(patterns: RegExp[], pathname: string): boolean {
  return patterns.some((p) => p.test(pathname));
}
